import React, { useEffect, useRef, Suspense, lazy, createElement } from 'react';
import { AdminCrmApp } from './components/admin/AdminCrmApp';
import { AdminLoginPage } from './components/admin/AdminLoginPage';
import { FloatingWhatsApp } from './components/FloatingWhatsApp';
import { Navbar } from './components/Navbar';
import { RestaurantsLanding } from './components/RestaurantsLanding';
import { SingleProductHero } from './components/SingleProductHero';
import { TrustStrip } from './components/TrustStrip';
import { FAQS, RESTAURANT_FAQS, SITE_CONFIG } from './config/siteConfig';
import { trackEvent } from './utils/analytics';
const ProblemSolutionSection = lazy(() => import('./components/ProblemSolutionSection').then(module => ({
  default: module.ProblemSolutionSection
})));
const ProductConfigurationsSection = lazy(() => import('./components/ProductConfigurationsSection').then(module => ({
  default: module.ProductConfigurationsSection
})));
const PortfolioSection = lazy(() => import('./components/PortfolioSection').then(module => ({
  default: module.PortfolioSection
})));
const WhatIncludesSection = lazy(() => import('./components/WhatIncludesSection').then(module => ({
  default: module.WhatIncludesSection
})));
const PackageLimitsSection = lazy(() => import('./components/PackageLimitsSection').then(module => ({
  default: module.PackageLimitsSection
})));
const ProcessSection = lazy(() => import('./components/ProcessSection').then(module => ({
  default: module.ProcessSection
})));
const PriceSection = lazy(() => import('./components/PriceSection').then(module => ({
  default: module.PriceSection
})));
const DiagnosticSection = lazy(() => import('./components/DiagnosticSection').then(module => ({
  default: module.DiagnosticSection
})));
const SingleProductFAQ = lazy(() => import('./components/SingleProductFAQ').then(module => ({
  default: module.SingleProductFAQ
})));
const FinalCTASection = lazy(() => import('./components/FinalCTASection').then(module => ({
  default: module.FinalCTASection
})));
const FooterSection = lazy(() => import('./components/FooterSection').then(module => ({
  default: module.FooterSection
})));
export function App() {
  const hasTrackedFifty = useRef(false);
  const hasTrackedNinety = useRef(false);
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isAdminRoute = normalizedPath === '/admin' || normalizedPath.startsWith('/admin/');
  const isAdminLoginRoute = normalizedPath === '/admin/login';
  const isRestaurantRoute = normalizedPath.endsWith('/restaurantes');
  const seo = isRestaurantRoute ? SITE_CONFIG.restaurantSeo : SITE_CONFIG.seo;
  const activeFaqs = isRestaurantRoute ? RESTAURANT_FAQS : FAQS;
  useEffect(() => {
    if (!isAdminRoute) return undefined;
    document.title = isAdminLoginRoute ? 'Acceso | TITÁN OS' : 'TITÁN OS';
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const createdRobotsTag = !robots;
    const previousRobotsContent = robots?.content;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = 'noindex,nofollow';
    return () => {
      if (createdRobotsTag) {
        robots?.remove();
      } else if (robots && previousRobotsContent !== undefined) {
        robots.content = previousRobotsContent;
      }
    };
  }, [isAdminLoginRoute, isAdminRoute]);
  useEffect(() => {
    if (isAdminRoute) return undefined;
    document.title = seo.title;
    const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (metaDescription) {
      metaDescription.content = seo.description;
    } else {
      const description = document.createElement('meta');
      description.name = 'description';
      description.content = seo.description;
      document.head.appendChild(description);
    }
    const metadata: Record<string, string> = {
      'og:title': seo.ogTitle,
      'og:description': seo.ogDescription,
      'og:type': 'website'
    };
    Object.entries(metadata).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}${isRestaurantRoute ? '/restaurantes' : '/'}`;
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'ProfessionalService',
        name: SITE_CONFIG.brandName,
        email: SITE_CONFIG.email,
        telephone: '+50685316690',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'CR'
        },
        areaServed: 'Costa Rica'
      }, {
        '@type': 'FAQPage',
        mainEntity: activeFaqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }]
    });
    document.head.appendChild(schema);
    if (SITE_CONFIG.analytics.metaPixelId && !(window as Window & {
      fbq?: unknown;
    }).fbq) {
      const pixelWindow = window as Window & {
        fbq?: ((...args: unknown[]) => void) & {
          queue?: unknown[][];
          loaded?: boolean;
          version?: string;
        };
        _fbq?: unknown;
      };
      const fbq = ((...args: unknown[]) => {
        fbq.queue?.push(args);
      }) as ((...args: unknown[]) => void) & {
        queue?: unknown[][];
        loaded?: boolean;
        version?: string;
      };
      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = '2.0';
      pixelWindow.fbq = fbq;
      pixelWindow._fbq = fbq;
      const pixelScript = document.createElement('script');
      pixelScript.async = true;
      pixelScript.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(pixelScript);
      fbq('init', SITE_CONFIG.analytics.metaPixelId);
      fbq('track', 'PageView');
    }
    function handleScrollAnalytics() {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;
      const progress = window.scrollY / maxScroll;
      if (progress >= 0.5 && !hasTrackedFifty.current) {
        hasTrackedFifty.current = true;
        trackEvent('scroll_50');
      }
      if (progress >= 0.9 && !hasTrackedNinety.current) {
        hasTrackedNinety.current = true;
        trackEvent('scroll_90');
      }
    }
    window.addEventListener('scroll', handleScrollAnalytics, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScrollAnalytics);
      schema.remove();
    };
  }, [activeFaqs, isAdminRoute, isRestaurantRoute, seo]);
  if (isAdminRoute) {
    return isAdminLoginRoute ? <AdminLoginPage data-id="element-0" /> : <AdminCrmApp data-id="element-1" />;
  }
  return <div className="min-h-screen overflow-x-hidden bg-[#050511] text-white selection:bg-[#a738f4] selection:text-white" data-id="element-2">
      <Navbar data-id="element-3" />
      {isRestaurantRoute ? <RestaurantsLanding data-id="element-4" /> : <main data-id="element-5">
          <SingleProductHero data-id="element-6" />
          <TrustStrip data-id="element-7" />
          <Suspense fallback={null} data-id="element-8">
            <ProblemSolutionSection data-id="element-9" />
            <ProductConfigurationsSection data-id="element-10" />
            <PortfolioSection data-id="element-11" />
            <WhatIncludesSection data-id="element-12" />
            <PackageLimitsSection data-id="element-13" />
            <ProcessSection data-id="element-14" />
            <PriceSection data-id="element-15" />
            <DiagnosticSection data-id="element-16" />
            <SingleProductFAQ data-id="element-17" />
            <FinalCTASection data-id="element-18" />
          </Suspense>
        </main>}
      <Suspense fallback={null} data-id="element-19">
        <FooterSection data-id="element-20" />
      </Suspense>
      <FloatingWhatsApp data-id="element-21" />
    </div>;
}